import { useEffect, useRef, useState } from 'react';
import { PolicyBadge } from '@voll-intelligence/intelligence-ui-kit';
import { useImageFallback } from '@voll-intelligence/ui-kit/hooks/use-image-fallback';
import {
  type ChatUiFlightReservation,
  type ChatUiFlightSegment,
  type ChatUiMoney,
  destinationImageByIata,
} from '@voll-intelligence/genui-contract';
import { FlightLeg, segmentStops } from '../../../shared/ui/flight_leg.js';
import { FlightLegStack, SegmentDetailDrawer } from '../../../shared/ui/flight_segment.js';
import { fmtMoney } from '../../../shared/ui/flight_format.js';
import { BuildingsIcon, NoteIcon, UserIcon } from '@phosphor-icons/react';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import { useGenUiTelemetry } from '../../../shared/ui/telemetry.js';
import { ReceiptRow, ValueRow } from '../../../shared/ui/receipt_rows.js';

type DetailTarget = { seg: ChatUiFlightSegment; dir: string };

/** Viajantes reservados, anotando quem regeu a política da cotação. O CPF só
 *  acompanha o viajante primário; os demais recebem o rótulo de política quando
 *  for o caso. */
function TravelerReceiptRows({
  travelers,
  policyTravelerId,
  passengerName,
  document: doc,
}: Pick<ChatUiFlightReservation, 'travelers' | 'policyTravelerId' | 'passengerName' | 'document'>) {
  const { t } = useGenUiI18n();
  const isLegacy = !travelers;
  const rows =
    travelers ?? (passengerName ? [{ id: 'legacy-passenger', name: passengerName }] : []);

  return rows.map((traveler, index) => {
    const showsDoc = Boolean(doc) && (isLegacy || index === 0);
    const isPolicyHolder = !isLegacy && traveler.id === policyTravelerId;
    return (
      <ReceiptRow
        key={traveler.id}
        icon={<UserIcon size={20} aria-hidden />}
        title={traveler.name}
        sub={
          showsDoc
            ? t('flight.reservation.cpf', { value: doc ?? '' })
            : isPolicyHolder
              ? t('traveler.policyHolder')
              : ''
        }
      />
    );
  });
}

function ReservationSegments({
  flight,
  returnFlight,
  legs,
  onDetail,
}: Pick<ChatUiFlightReservation, 'flight' | 'returnFlight' | 'legs'> & {
  readonly onDetail: (detail: DetailTarget) => void;
}) {
  const { t } = useGenUiI18n();
  if (legs) {
    return (
      <FlightLegStack>
        {legs.map((leg, index) => {
          const dir = t('flight.reservation.leg', { n: index + 1 });
          return (
            <FlightLeg
              key={`${leg.flight.from}-${leg.flight.to}-${leg.flight.departAt}`}
              seg={leg.flight}
              stops={segmentStops(leg.flight)}
              kindLabel={dir}
              showCarrier
              onDetail={() => {
                onDetail({ seg: leg.flight, dir });
              }}
            />
          );
        })}
      </FlightLegStack>
    );
  }
  return (
    <FlightLegStack>
      {flight && (
        <FlightLeg
          seg={flight}
          stops={segmentStops(flight)}
          showCarrier
          onDetail={() => {
            onDetail({
              seg: flight,
              dir: returnFlight ? t('flight.quote.outbound') : '',
            });
          }}
        />
      )}
      {returnFlight && (
        <FlightLeg
          seg={returnFlight}
          stops={segmentStops(returnFlight)}
          showCarrier
          onDetail={() => {
            onDetail({ seg: returnFlight, dir: t('flight.quote.return') });
          }}
        />
      )}
    </FlightLegStack>
  );
}

/** Viajantes + rótulos corporativos. Some inteiro (com a divisória) quando não
 *  há nenhum dos quatro — mesma guarda do `StayPeople` do recibo de hotel. */
function ReservationPeople({ reservation }: { readonly reservation: ChatUiFlightReservation }) {
  const { t } = useGenUiI18n();
  const { travelers, policyTravelerId, passengerName, document, costCenter, motive } = reservation;
  if (!travelers && !passengerName && !costCenter && !motive) return null;
  return (
    <>
      <div className="h-px bg-border" />
      <div className="flex flex-col gap-2.5">
        <TravelerReceiptRows
          travelers={travelers}
          policyTravelerId={policyTravelerId}
          passengerName={passengerName}
          document={document}
        />
        {costCenter && (
          <ReceiptRow
            icon={<BuildingsIcon size={20} aria-hidden />}
            title={costCenter}
            sub={t('traveler.costCenter')}
          />
        )}
        {motive && (
          <ReceiptRow
            icon={<NoteIcon size={20} aria-hidden />}
            title={motive}
            sub={t('traveler.motive')}
          />
        )}
      </div>
    </>
  );
}

/**
 * Tarifa + Taxas e encargos = Total. "Taxas e encargos" deriva de
 * `total − baseFare` (mesma moeda); sem `baseFare` mostra só o Total, e aí o
 * Total perde a régua de topo — quem separa é a divisória do bloco, senão a
 * linha dobra.
 *
 * Mora aqui porque ESTE card é o único consumidor.
 */
function FareBreakdown({
  total,
  baseFare,
}: {
  readonly total: ChatUiMoney;
  readonly baseFare?: ChatUiMoney;
}) {
  const { t } = useGenUiI18n();
  const taxes = baseFare?.currency === total.currency ? total.amount - baseFare.amount : null;
  // Tarifa base negativa (ou taxas negativas) = dado upstream inválido → cai no
  // layout só-Total em vez de mostrar uma "Tarifa" negativa + taxas infladas.
  const showBreakdown = baseFare != null && baseFare.amount >= 0 && taxes !== null && taxes >= 0;
  return (
    <>
      <div className="h-px bg-border" />
      <div className="flex flex-col gap-2.5">
        {showBreakdown && (
          <>
            <ValueRow label={t('flight.fareBreakdown.fare')} value={fmtMoney(baseFare)} />
            <ValueRow
              label={t('flight.fareBreakdown.taxes')}
              value={fmtMoney({ amount: taxes, currency: total.currency })}
            />
          </>
        )}
        <div
          className={
            showBreakdown
              ? 'flex items-baseline justify-between gap-3 border-t border-border pt-2.5'
              : 'flex items-baseline justify-between gap-3'
          }
        >
          <span className="text-title text-foreground">
            {t('flight.fareBreakdown.totalPerPerson')}
          </span>
          <span className="text-title text-foreground">{fmtMoney(total)}</span>
        </div>
      </div>
    </>
  );
}

/**
 * Bilhete de reserva (`flight_reservation`) — estado terminal do fluxo, sempre
 * display-only: banda do destino, escudo de política, trechos (ida/volta) com
 * "Detalhes", viajante, breakdown de valores. O localizador NÃO é exibido no
 * card (vai por e-mail). Sem callback de ação.
 *
 * Escrito sobre os primitivos do recibo (`ReceiptRow`/`ValueRow`), em
 * utilitária sobre os tokens do DS — as folhas `.frv-*`/`.fqc-*` morreram
 * com este porte. O TRECHO segue sendo a `FlightLeg` compartilhada com a lista
 * de ofertas: portá-la arrastaria `flight_offers` junto, e é outro trabalho.
 *
 * `confirmed` não escreve status — o recibo já é a afirmação. `pending` fecha
 * com a nota de estado, senão uma reserva cujo PNR ainda não saiu fica idêntica
 * a uma confirmada (a nota do agente é texto livre e opcional, não pode ser o
 * que carrega isso).
 */
export function FlightReservationCard({
  reservation,
  note,
}: {
  readonly reservation: ChatUiFlightReservation;
  readonly note?: string;
}) {
  const { t } = useGenUiI18n();
  const telemetry = useGenUiTelemetry();
  const [detail, setDetail] = useState<DetailTarget | null>(null);
  // Telemetria por TRANSIÇÃO (null → aberto), não por render nem por clique: um
  // segundo "Detalhes" trocando de trecho enquanto o drawer já está aberto
  // não reabre nada — o mesmo drawer só troca de conteúdo.
  const wasDetailOpen = useRef(false);
  useEffect(() => {
    const isOpen = detail !== null;
    if (isOpen && !wasDetailOpen.current) {
      telemetry.track('drawer_opened', { kind: 'flight_detail' });
    }
    wasDetailOpen.current = isOpen;
  }, [detail, telemetry]);
  const { status, price, baseFare, flight, returnFlight, legs, destinationImageUrl, withinPolicy } =
    reservation;
  // Foto do destino: campo do backend quando vier; senão resolve no front pelo
  // IATA de chegada (mapa estático). No multi-trecho usa o destino final (último
  // trecho). Ausente → o card fecha no topo, sem banda.
  const destImage = useImageFallback(
    destinationImageUrl ?? destinationImageByIata(legs ? legs.at(-1)?.flight.to : flight?.to),
  );

  return (
    <>
      {/* Medidas do recibo de voo (`.frv`/`.frv-body`, mortas com o porte): 520px
          de largura de leitura, corpo em degraus de 14px com padding 16/18/18,
          raio `rounded-md` do checkout — não o `rounded-card` (34px) da conversa.
          A foto do destino vaza para a borda, então fica FORA do padding. */}
      <section
        className="mx-auto w-full max-w-[520px] overflow-hidden rounded-md border border-border bg-card shadow-(--shadow-elevation)"
        aria-label={t('flight.reservation.aria')}
      >
        {destImage.src && (
          <img
            src={destImage.src}
            alt={t('flight.reservation.aria')}
            onError={destImage.onError}
            className="h-[150px] w-full object-cover"
          />
        )}
        <div className="space-y-3.5 px-[18px] pt-4 pb-[18px]">
          {/* Selo de política (recibo corporativo): `PolicyBadge`, o mesmo do
              recibo de hotel — agora um componente, não uma repetição. */}
          {withinPolicy && <PolicyBadge tone="within" label={t('booking.inPolicy')} />}

          <ReservationSegments
            flight={flight}
            returnFlight={returnFlight}
            legs={legs}
            onDetail={setDetail}
          />
          <ReservationPeople reservation={reservation} />
          <FareBreakdown total={price} baseFare={baseFare} />

          {status === 'pending' && (
            <p className="text-detail text-muted-foreground">{t('flight.reservation.emailSent')}</p>
          )}
          {note && <p className="text-detail whitespace-pre-wrap text-muted-foreground">{note}</p>}
        </div>
      </section>

      {detail && (
        <SegmentDetailDrawer
          seg={detail.seg}
          dir={detail.dir}
          onClose={() => {
            setDetail(null);
          }}
        />
      )}
    </>
  );
}
